import { Dialog } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import YupPassword from 'yup-password';

import { Modal } from '@/components/common/modal';
import { Button } from '@/components/form/button';
import { Input } from '@/components/form/input';

import { changeCurrentPassword } from '../services/auth';

// extend yup to support password validations
YupPassword(yup);

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close */
  toggle: () => void;

  /** logout, if user is logged in with expired token */
  logout: () => void;
}

export type ResetPasswordFormFields = {
  currentPassword: string;
  password1: string;
  password2: string;
};

const validationSchema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  password1: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must contain 8 or more characters.')
    .minLowercase(1, 'Password must contain a minimum of 1 lower case letter [a-z]')
    .minUppercase(1, 'Password must contain a minimum of 1 upper case letter [A-Z].')
    .minNumbers(1, 'Password must contain a minimum of 1 numeric character [0-9].'),
  password2: yup
    .string()
    .required('Password confirmation is required')
    .oneOf([yup.ref('password1'), null], 'Password confirmation must match password.'),
});

export const PasswordChangeModal: React.FC<IProps> = ({ isOpen, toggle, logout }) => {
  const { t } = useTranslation('translation', { useSuspense: false });
  const [error, setError] = useState('');

  const updatePasswordMutation = useMutation({
    mutationFn: ({
      ...data
    }: {
      currentPassword: string;
      password1: string;
      password2: string;
    }) => changeCurrentPassword({ ...data }),
    onSuccess: () => {
      toast.success(t('Your password has been changed successfully.'));
      onReset();
    },
    onError: async (e) => {
      if (e instanceof HTTPError) {
        if (e.response.status === 401) {
          logout();
          return;
        }
        if (e.response.status === 400) {
          const data = await e.response.json();
          setError(data.message);
          return;
        }
      }
      setError('An error occurred. Please try again.');
    },
  });

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormFields>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit((data) => {
    updatePasswordMutation.mutate({ ...data });
  });

  const onReset = () => {
    reset();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      title={t('forgotPassword.changePasswordTitle')}
      conatinerClassnames="lg:w-1/2"
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 py-6">
          <Dialog.Description>
            You must provide your current password in order to change it.
          </Dialog.Description>
          <Input<ResetPasswordFormFields>
            variant="solid"
            type="password"
            name="currentPassword"
            className="w-72"
            autoComplete="password"
            placeholder={t('Current password')}
            register={register}
            errors={errors}
          />
          <Input<ResetPasswordFormFields>
            variant="solid"
            type="password"
            name="password1"
            className="w-72"
            autoComplete="new-password"
            placeholder={t('New password')}
            register={register}
            errors={errors}
          />
          <Input<ResetPasswordFormFields>
            variant="solid"
            type="password"
            name="password2"
            className="w-72"
            autoComplete="new-password"
            placeholder={t('Confirm new password')}
            register={register}
            errors={errors}
          />
        </div>
        {error && <p className="text-red-500">{error}</p>}
        <div className="flex justify-end">
          <Button type="submit" isLoading={updatePasswordMutation.isLoading}>
            {t('Update password')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
